import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UserRole } from '@cavaquinho/shared';
import { useCurrentUser } from '../../app/AuthProvider.tsx';
import { api } from '../../lib/api/client.js';
import type {
  AdminUser,
  AdminUserGrowthPoint,
  AdminUserSummary,
  AdminUsersQuery,
} from '../../lib/api/types.js';
import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Input } from '../../ui/primitives/Input.tsx';
import { LiveRegion } from '../../ui/primitives/LiveRegion.tsx';
import { Select } from '../../ui/primitives/Select.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { Dialog } from '../../ui/components/Dialog/Dialog.tsx';
import { ColumnChart, type ColumnPoint } from '../../ui/components/Chart/ColumnChart.tsx';
import { LoadingState } from '../../ui/components/LoadingState/LoadingState.tsx';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { ProportionBar } from '../../ui/components/Chart/ProportionBar.tsx';
import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from '../../ui/components/DataTable/DataTable.tsx';
import { Pagination } from '../../ui/components/Pagination/Pagination.tsx';
import { StatRow, StatTile } from '../../ui/components/StatTile/StatTile.tsx';
import { Toolbar } from '../../ui/components/Toolbar/Toolbar.tsx';
import {
  adminWriteRefusal,
  announceAdminAlertsChanged,
  FROZEN_MESSAGE,
  REAUTH_MESSAGE,
  signInAgain,
} from './admin-security.ts';
import styles from './admin.module.css';

/**
 * Every account on the platform, as an administrative table
 * (`GET /api/admin/users`; design system § 14 Data table, § 16 density).
 *
 * THE ONE THING IT CHANGES IS THE ROLE, AND THERE IS STILL NO "ADD USER".
 * Accounts are created by the people who own them, through sign-up, so a
 * create button here would be a promise the API does not keep. What an
 * administrator does need is to bring a second administrator in without an
 * operator at a shell, and that is the row action: grant the role, or take it
 * back (`PATCH /api/admin/users/:id/role`).
 *
 * NOBODY'S OWN ROW CARRIES THE CONTROL. The server refuses a caller changing
 * their own role, and the screen does not offer it in the first place — an
 * administrator cannot lock themselves out of the panel they are standing in,
 * and because of that the platform can never reach zero administrators. The
 * row says "Você" where the button would be, so the absence reads as a fact
 * about the row rather than as something that failed to load.
 *
 * A FROZEN ADMINISTRATOR'S ROW CARRIES "LIBERAR" (FR-084). The brake in the
 * API stops an administrator who changed more in a window than the limits
 * allow; another administrator looks at Atividade and lifts it here. Granting,
 * withdrawing and lifting all ask for a Google sign-in from the last few
 * minutes — when the session is older the dialog says so and sends the person
 * through Google, and they confirm again on the way back.
 *
 * NAME, E-MAIL AND VERIFICATION STAY UNEDITABLE. Those belong to the person
 * who owns the account; the role is a decision about the platform, which is
 * why it is the only column this screen writes.
 *
 * EVERY FIGURE ON IT IS A COUNT THE DATABASE HOLDS.
 * `emailVerified` is the platform's only account status, `lastSignInAt` is the
 * newest row in `session`, and the growth series is `created_at` grouped by
 * month. There is no "active users", no engagement and no churn, because
 * nothing records them — an invented number on this screen is one an operator
 * would make a decision from.
 *
 * THE QUERY IS THE SERVER'S. Search, filter, sort and page all travel as query
 * parameters and come back as one page plus a total, so the screen costs the
 * same at two accounts as at ten thousand. Nothing here slices an array.
 */
interface UsersData {
  users: AdminUser[];
  summary: AdminUserSummary | null;
  growth: AdminUserGrowthPoint[];
  total: number;
  pageCount: number;
}

const EMPTY_USERS: UsersData = {
  users: [],
  summary: null,
  growth: [],
  total: 0,
  pageCount: 1,
};

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador',
  learner: 'Aluno',
};

/** The default, and the one place the page size is written down. */
const PAGE_SIZES = [10, 20, 50] as const;

/*
 * `01/09/2026`, not `1 de set. de 2026`.
 *
 * A date in a table is read as a COLUMN — the reader compares the row above
 * with the row below — and the numeric form is both half the width and
 * vertically alignable, which is the whole reason § 14 gives numeric columns
 * tabular figures. The spelled-out form belongs where a date is read as part
 * of a sentence, which is where the learner's screens use it.
 */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/** `YYYY-MM` as the axis tick and as the tooltip's own longer form. */
function monthPoint(point: AdminUserGrowthPoint): ColumnPoint {
  const [year, month] = point.month.split('-').map(Number);
  const at = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1));
  return {
    label: at.toLocaleDateString('pt-BR', { month: 'short', timeZone: 'UTC' }),
    caption: at.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    value: point.created,
  };
}

/**
 * The act an open confirmation is about, with the account held whole rather
 * than as an id: the dialog names the person, and re-finding the row in a list
 * that may have been re-fetched underneath it would sometimes name nobody.
 */
interface PendingChange {
  user: AdminUser;
  kind: 'grant' | 'revoke' | 'unfreeze';
}

const DIALOG: Record<
  PendingChange['kind'],
  { title: string; confirm: string; body: (name: string) => string }
> = {
  grant: {
    title: 'Tornar administrador?',
    confirm: 'Tornar administrador',
    body: (name) =>
      `${name} passa a ver e editar as lições, os exercícios e esta lista de contas. O acesso vale já no próximo carregamento, e você pode removê-lo depois.`,
  },
  revoke: {
    title: 'Remover o acesso?',
    confirm: 'Remover acesso',
    body: (name) =>
      `${name} deixa de ver o painel, volta a ser aluno e é desconectado de todos os aparelhos. O progresso e os dados da conta ficam como estão, e você pode conceder o acesso de novo.`,
  },
  unfreeze: {
    title: 'Liberar as alterações?',
    confirm: 'Liberar',
    body: (name) =>
      `${name} volta a poder alterar o conteúdo e as contas. Antes de liberar, confira em Atividade o que foi feito por essa conta.`,
  },
};

export function UsersList() {
  const me = useCurrentUser();
  /** The confirmation on screen, or null. */
  const [asking, setAsking] = useState<PendingChange | null>(null);
  const [saving, setSaving] = useState(false);
  /** The outcome of the last change — announced, and shown. */
  const [outcome, setOutcome] = useState('');
  const [failure, setFailure] = useState('');
  /** The last attempt needed a fresh Google sign-in; the confirm button now starts one. */
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[1]);
  /** What is in the box right now — the request follows it a beat later. */
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<UserRole | 'all'>('all');
  const [verification, setVerification] = useState<'all' | 'verified' | 'pending'>('all');
  const [sort, setSort] = useState<DataTableSort>({ columnId: 'createdAt', direction: 'desc' });

  /*
   * Typing is not a request per keystroke. The box updates immediately and the
   * query follows once the person has stopped, which is what keeps a search
   * over ten thousand accounts from firing ten thousand times.
   */
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  /* A narrower filter can leave the current page past the end of the result,
     so every change to what is being asked for returns to the first page. */
  useEffect(() => {
    setPage(1);
  }, [query, role, verification, pageSize]);

  const request: AdminUsersQuery = useMemo(
    () => ({
      page,
      pageSize,
      search: query,
      role,
      status: verification,
      sort: sort.columnId as AdminUsersQuery['sort'],
      direction: sort.direction,
    }),
    [page, pageSize, query, role, verification, sort],
  );

  const loadUsers = useCallback(
    async (): Promise<UsersData> => {
      const response = await api.adminUsers(request);
      return {
        users: response.users,
        summary: response.summary,
        growth: response.growth,
        total: response.total,
        pageCount: response.pageCount,
      };
    },
    [request],
  );

  // A response that arrives after a newer request started (a slow page-1
  // landing after a fast page-2) is dropped by `useAsyncData` itself, never
  // overwriting the table with a page the bar says it is not on.
  const {
    status,
    data: { users, summary, growth, total, pageCount },
    showLoading,
    refreshing: busy,
    reload,
  } = useAsyncData(loadUsers, EMPTY_USERS, [request]);

  /** A second click on the sorted column reverses it; a first click on any
      other one starts descending, which is what an administrator wants from
      a date and does no harm on a name. */
  const changeSort = useCallback((columnId: string) => {
    setSort((current) =>
      current.columnId === columnId
        ? { columnId, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { columnId, direction: 'desc' },
    );
    setPage(1);
  }, []);

  /**
   * Applies the change the dialog was asking about, then re-reads the page.
   *
   * RE-READS RATHER THAN PATCHING THE ROW IN PLACE. The response carries the
   * account's new role, but the tiles above the table count administrators
   * and learners, and the toolbar may be filtering by the very role that just
   * changed — a row edited locally would leave "Administradores: 1" beside two
   * of them, or leave a row sitting in a filter it no longer matches.
   *
   * `force` because the request has not changed: same page, same filter. Only
   * the answer has.
   */
  const commitChange = useCallback(async () => {
    if (!asking) return;
    const { user: account, kind } = asking;

    setSaving(true);
    setFailure('');
    try {
      if (kind === 'unfreeze') {
        await api.unfreezeUser(account.id);
        announceAdminAlertsChanged();
      } else {
        await api.updateUserRole(account.id, { role: kind === 'grant' ? 'admin' : 'learner' });
      }
      setAsking(null);
      setNeedsSignIn(false);
      setOutcome(
        kind === 'grant'
          ? `${account.name} agora é administrador.`
          : kind === 'revoke'
            ? `${account.name} agora é aluno.`
            : `${account.name} pode alterar de novo.`,
      );
      await reload({ force: true });
    } catch (error) {
      // The dialog STAYS OPEN on a failure, holding the decision the person
      // already made, so retrying is one click rather than finding the row
      // again. Saying nothing and closing would look like it worked.
      const refusal = adminWriteRefusal(error);
      setNeedsSignIn(refusal === 'reauth');
      setFailure(
        refusal === 'reauth'
          ? REAUTH_MESSAGE
          : refusal === 'frozen'
            ? FROZEN_MESSAGE
            : 'Não foi possível salvar a alteração. Tente novamente.',
      );
    } finally {
      setSaving(false);
    }
  }, [asking, reload]);

  const closeDialog = useCallback(() => {
    setAsking(null);
    setFailure('');
    setNeedsSignIn(false);
  }, []);

  const startSignIn = useCallback(() => {
    setSaving(true);
    signInAgain().catch(() => {
      setSaving(false);
      setFailure('Não foi possível abrir a entrada com o Google. Tente novamente.');
    });
  }, []);

  const columns: DataTableColumn<AdminUser>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Conta',
        sortable: true,
        rowHeader: true,
        cell: (account) => account.name,
      },
      {
        id: 'email',
        header: 'E-mail',
        sortable: true,
        cell: (account) => account.email,
      },
      {
        id: 'role',
        header: 'Função',
        sortable: true,
        width: 'min',
        cell: (account) => (
          <span className={styles.badges}>
            <Badge tone={account.role === 'admin' ? 'info' : 'neutral'}>
              {ROLE_LABEL[account.role] ?? account.role}
            </Badge>
            {account.role === 'admin' && account.adminFrozenAt ? (
              <Badge tone="near-miss" icon="alert-circle">
                Congelado
              </Badge>
            ) : null}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: 'min',
        cell: (account) =>
          account.emailVerified ? (
            <Badge tone="success" icon="check">
              Verificada
            </Badge>
          ) : (
            <Badge tone="near-miss">Pendente</Badge>
          ),
      },
      {
        id: 'createdAt',
        header: 'Criada em',
        sortable: true,
        width: 'min',
        align: 'end',
        cell: (account) => (
          <span className="type-numeric-inline">{formatDate(account.createdAt)}</span>
        ),
      },
      {
        id: 'lastSignInAt',
        header: 'Último acesso',
        sortable: true,
        width: 'min',
        // Never a fabricated date: an account with no session row has not
        // signed in, and the column says so in words.
        align: 'end',
        cell: (account) =>
          account.lastSignInAt ? (
            <span className="type-numeric-inline">{formatDate(account.lastSignInAt)}</span>
          ) : (
            <Text as="span" variant="body-sm" tone="muted">
              Nunca
            </Text>
          ),
      },
      {
        id: 'actions',
        header: 'Função',
        width: 'min',
        align: 'end',
        cell: (account) => {
          /*
           * The signed-in administrator's own row says who it is instead of
           * offering a control. The server refuses a self-change anyway; this
           * is so nobody reaches for a button that was only ever going to say
           * no, and so the row reads as "this is you" rather than as an action
           * that is mysteriously missing.
           */
          if (account.id === me?.id) {
            return (
              <Text as="span" variant="body-sm" tone="muted">
                Você
              </Text>
            );
          }

          const promoting = account.role !== 'admin';
          const frozen = account.role === 'admin' && account.adminFrozenAt !== null;
          return (
            <div className={styles.rowActions}>
              {frozen ? (
                <Button
                  variant="secondary"
                  size="compact"
                  ariaLabel={`Liberar as alterações de ${account.name}`}
                  onClick={() => {
                    setFailure('');
                    setNeedsSignIn(false);
                    setAsking({ user: account, kind: 'unfreeze' });
                  }}
                >
                  Liberar
                </Button>
              ) : null}
              <Button
                variant={promoting ? 'secondary' : 'ghost'}
                size="compact"
                icon={promoting ? 'shield' : undefined}
                // The label names the ACCOUNT, because a table of forty rows
                // holds forty buttons reading "Tornar administrador" and a
                // screen reader moving between them would hear no difference.
                ariaLabel={
                  promoting
                    ? `Tornar ${account.name} administrador`
                    : `Remover o acesso de administrador de ${account.name}`
                }
                onClick={() => {
                  setFailure('');
                  setNeedsSignIn(false);
                  setAsking({ user: account, kind: promoting ? 'grant' : 'revoke' });
                }}
              >
                {promoting ? 'Tornar administrador' : 'Remover acesso'}
              </Button>
            </div>
          );
        },
      },
    ],
    [me?.id],
  );

  if (status === 'loading') return showLoading ? <LoadingState label="Carregando os usuários" /> : null;

  if (status === 'unavailable') {
    return (
      <div className={styles.page}>
        <Text as="h1" variant="subheading">
          Não foi possível carregar os usuários
        </Text>
        <span>
          <Button onClick={() => reload({ force: true })}>Tentar novamente</Button>
        </span>
      </div>
    );
  }

  const filtered = query !== '' || role !== 'all' || verification !== 'all';

  return (
    <div className={styles.page} data-testid="admin-users-list">
      <header className={styles.header}>
        <div className={styles.heading}>
          <Text as="h1" variant="subheading">
            Usuários
          </Text>
          <Text as="p" variant="body-sm" tone="muted">
            Todas as contas da plataforma. Você pode tornar outra pessoa administradora — a sua
            própria função só muda pela mão de outro administrador. Alterar funções pede uma
            entrada recente com o Google.
          </Text>
        </div>
      </header>

      {summary ? (
        <StatRow label="Resumo das contas">
          <StatTile
            label="Contas"
            value={summary.total}
            hint="Total na plataforma"
            icon="user"
          />
          <StatTile
            label="Administradores"
            value={summary.admins}
            hint="Com acesso ao painel"
            icon="shield"
          />
          <StatTile label="Alunos" value={summary.learners} hint="Sem acesso ao painel" />
          <StatTile
            label="Novas em 30 dias"
            value={summary.recent}
            hint="Criadas desde o mês passado"
          />
        </StatRow>
      ) : null}

      <div className={styles.charts}>
        <ColumnChart
          title="Novas contas por mês"
          description="Últimos seis meses, pela data de criação."
          points={growth.map(monthPoint)}
          valueLabel="Novas contas"
          emptyMessage="Nenhuma conta criada nos últimos seis meses."
        />
        {summary ? (
          <ProportionBar
            title="Verificação de e-mail"
            itemLabel="Contas"
            emptyMessage="Nenhuma conta ainda."
            segments={[
              { id: 'verified', label: 'Verificadas', value: summary.verified, series: 2 },
              { id: 'pending', label: 'Pendentes', value: summary.pending, series: 1 },
            ]}
          />
        ) : null}
      </div>

      <section className={styles.section}>
        <Toolbar label="Filtrar usuários">
          <Input
            id="users-search"
            type="search"
            size="compact"
            icon="search"
            ariaLabel="Buscar por nome ou e-mail"
            placeholder="Buscar por nome ou e-mail"
            value={search}
            onChange={setSearch}
          />
          <Select
            id="users-role"
            size="compact"
            ariaLabel="Filtrar por função"
            value={role}
            options={[
              { value: 'all', label: 'Todas as funções' },
              { value: 'admin', label: 'Administrador' },
              { value: 'learner', label: 'Aluno' },
            ]}
            onChange={(next) => setRole(next as UserRole | 'all')}
          />
          <Select
            id="users-status"
            size="compact"
            ariaLabel="Filtrar por status"
            value={verification}
            options={[
              { value: 'all', label: 'Todos os status' },
              { value: 'verified', label: 'Verificada' },
              { value: 'pending', label: 'Pendente' },
            ]}
            onChange={(next) => setVerification(next as 'all' | 'verified' | 'pending')}
          />
          {filtered ? (
            <Button
              variant="ghost"
              size="compact"
              onClick={() => {
                setSearch('');
                setRole('all');
                setVerification('all');
              }}
            >
              Limpar
            </Button>
          ) : null}
        </Toolbar>

        <DataTable
          caption="Contas da plataforma"
          testId="users"
          rowTestId="user-row"
          columns={columns}
          rows={users}
          rowKey={(account) => account.id}
          sort={sort}
          onSortChange={changeSort}
          busy={busy}
          empty={
            <Text as="p" variant="body-sm" tone="muted">
              {filtered
                ? 'Nenhuma conta corresponde a esses filtros.'
                : 'Nenhuma conta ainda.'}
            </Text>
          }
        />

        <LiveRegion message={outcome} />

        <Pagination
          label="Páginas de usuários"
          itemLabel="usuários"
          page={page}
          pageCount={pageCount}
          total={total}
          pageSize={pageSize}
          pageSizes={PAGE_SIZES}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </section>

      {/*
        * A DIALOG, NOT AN IMMEDIATE TOGGLE. Granting the administrator role
        * hands somebody every authoring screen and this table; withdrawing it
        * takes them away from a person who may be in the middle of using
        * them. Neither is a thing to do by brushing a control in a row of
        * forty — but both are reversible by the person confirming, so the
        * ceremony stops at one sentence and a named button.
        *
        * The sentence says what the role CAN DO, not that it is dangerous:
        * the administrator weighing this is asking "what am I handing over",
        * and the answer makes the decision easier rather than scarier.
        */}
      <Dialog
        open={asking !== null}
        title={asking ? DIALOG[asking.kind].title : ''}
        testId="role-confirm"
        onClose={() => {
          if (!saving) closeDialog();
        }}
        actions={
          <>
            <span data-testid="role-cancel">
              {saving ? (
                <Button variant="ghost" disabled disabledReason="Salvando a alteração.">
                  Cancelar
                </Button>
              ) : (
                <Button variant="ghost" onClick={closeDialog}>
                  Cancelar
                </Button>
              )}
            </span>
            <span data-testid="role-confirm-action">
              {saving ? (
                <Button
                  variant={asking?.kind === 'revoke' ? 'destructive' : 'primary'}
                  disabled
                  disabledReason="Salvando a alteração."
                >
                  Salvando…
                </Button>
              ) : needsSignIn ? (
                // The act was refused for want of a recent sign-in. Google
                // brings the person back to this screen, where they confirm
                // again with a session that now qualifies.
                <Button variant="primary" onClick={startSignIn}>
                  Entrar com o Google
                </Button>
              ) : (
                <Button
                  variant={asking?.kind === 'revoke' ? 'destructive' : 'primary'}
                  onClick={() => void commitChange()}
                >
                  {asking ? DIALOG[asking.kind].confirm : ''}
                </Button>
              )}
            </span>
          </>
        }
      >
        {asking ? (
          <>
            {DIALOG[asking.kind].body(asking.user.name)}
            {failure ? (
              <Text as="p" variant="body-sm" tone="error">
                {failure}
              </Text>
            ) : null}
          </>
        ) : null}
      </Dialog>
    </div>
  );
}
